if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mediaItems = [];
        this.currentIndex = 0;
        this.isGalleryMode = false;
        this.galleryInitialized = false;
        this.isZoomed = false;
        this.zoomScale = 4; // Zoom magnification level
        this.boundKeydownHandler = null;

        // For gallery mode, we need to override the default pointerup behavior
        // The parent class adds a pointerup listener that closes on any click
        // We'll handle this in initializeGalleryModal
      }

      disconnectedCallback() {
        // Clean up event listeners when element is removed
        if (this.boundKeydownHandler) {
          this.removeEventListener('keydown', this.boundKeydownHandler);
          this.boundKeydownHandler = null;
        }
        // Reset initialization flag so listeners can be re-attached if element is re-added
        this.galleryInitialized = false;
        this.mediaItems = [];
      }

      hide() {
        super.hide();
        window.pauseAllMedia();
        // Reset zoom state when closing modal
        this.resetZoom();
      }

      resetZoom() {
        this.isZoomed = false;
        const zoomedImg = this.querySelector('.product-media-modal__media-item.active img');
        if (zoomedImg) {
          zoomedImg.classList.remove('zoomed');
          zoomedImg.style.transform = '';
          zoomedImg.style.cursor = 'zoom-in';
        }
      }

      show(opener) {
        super.show(opener);
        this.isGalleryMode = this.classList.contains('product-media-modal--gallery');

        if (this.isGalleryMode) {
          this.initializeGalleryModal();
          this.showActiveMedia();
        } else {
          this.showActiveMediaLightbox();
        }
      }

      initializeGalleryModal() {
        // Only initialize once to avoid duplicate event listeners
        if (this.galleryInitialized) return;
        this.galleryInitialized = true;

        // Get all media items in the content area (excluding thumbnails)
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return;

        this.mediaItems = Array.from(content.querySelectorAll('.product-media-modal__media-item'));

        // Prevent the dialog from closing when clicking inside the wrapper
        const dialog = this.querySelector('.product-media-modal__dialog');
        if (dialog) {
          dialog.addEventListener('pointerup', (e) => {
            e.stopPropagation();
          });
          dialog.addEventListener('click', (e) => {
            e.stopPropagation();
          });
        }

        // Set up thumbnail click handlers
        this.querySelectorAll('.product-media-modal__thumbnail').forEach((thumbnail) => {
          thumbnail.addEventListener('pointerup', (e) => {
            e.stopPropagation();
            e.preventDefault();
          });
          thumbnail.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
            const mediaId = thumbnail.getAttribute('data-media-id');
            this.setActiveMedia(mediaId);
          });
        });

        // Set up navigation arrows
        const prevBtn = this.querySelector('.product-media-modal__nav--prev');
        const nextBtn = this.querySelector('.product-media-modal__nav--next');

        if (prevBtn) {
          prevBtn.addEventListener('pointerup', (e) => e.stopPropagation());
          prevBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigatePrev();
          });
        }
        if (nextBtn) {
          nextBtn.addEventListener('pointerup', (e) => e.stopPropagation());
          nextBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigateNext();
          });
        }

        // Set up image zoom functionality
        this.setupImageZoom();

        // Keyboard navigation
        this.boundKeydownHandler = (e) => {
          if (e.key === 'ArrowLeft') {
            this.navigatePrev();
          } else if (e.key === 'ArrowRight') {
            this.navigateNext();
          }
        };
        this.addEventListener('keydown', this.boundKeydownHandler);
      }

      showActiveMediaLightbox() {
        // Original lightbox behavior - scroll to the clicked image
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      showActiveMedia() {
        const openedByMediaId = this.openedBy.getAttribute('data-media-id');
        this.setActiveMedia(openedByMediaId);
      }

      setActiveMedia(mediaId) {
        // Reset zoom when switching images
        this.resetZoom();

        // Refresh media items in case they weren't loaded initially
        if (this.mediaItems.length === 0) {
          const content = this.querySelector('.product-media-modal__content');
          this.mediaItems = Array.from(content.querySelectorAll('.product-media-modal__media-item'));
        }

        // Hide all media items and reset any zoom transforms
        this.mediaItems.forEach((item, index) => {
          item.classList.remove('active');
          const img = item.querySelector('img');
          if (img) {
            img.classList.remove('zoomed');
            img.style.transform = '';
            img.style.cursor = 'zoom-in';
          }
          if (item.getAttribute('data-media-id') === mediaId) {
            this.currentIndex = index;
          }
        });

        // Show the selected media
        const activeMedia = this.querySelector(`.product-media-modal__media-item[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;

        activeMedia.classList.add('active');

        // Update thumbnail active state
        this.querySelectorAll('.product-media-modal__thumbnail').forEach((thumbnail) => {
          thumbnail.classList.remove('active');
          thumbnail.removeAttribute('aria-current');
          if (thumbnail.getAttribute('data-media-id') === mediaId) {
            thumbnail.classList.add('active');
            thumbnail.setAttribute('aria-current', 'true');
            // Scroll thumbnail into view
            thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        });

        // Handle deferred media (videos)
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      navigatePrev() {
        if (this.mediaItems.length <= 1) return;
        this.currentIndex = (this.currentIndex - 1 + this.mediaItems.length) % this.mediaItems.length;
        const mediaId = this.mediaItems[this.currentIndex].getAttribute('data-media-id');
        this.setActiveMedia(mediaId);
      }

      navigateNext() {
        if (this.mediaItems.length <= 1) return;
        this.currentIndex = (this.currentIndex + 1) % this.mediaItems.length;
        const mediaId = this.mediaItems[this.currentIndex].getAttribute('data-media-id');
        this.setActiveMedia(mediaId);
      }

      setupImageZoom() {
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return;

        // Add click handler to toggle zoom on images
        content.addEventListener('click', (e) => {
          const img = e.target.closest('.product-media-modal__media-item img');
          if (!img) return;

          e.stopPropagation();

          if (this.isZoomed) {
            // Zoom out
            this.isZoomed = false;
            img.classList.remove('zoomed');
            img.style.transform = '';
            img.style.cursor = 'zoom-in';
          } else {
            // Zoom in
            this.isZoomed = true;
            img.classList.add('zoomed');
            img.style.cursor = 'zoom-out';
            // Calculate initial zoom position based on click location
            this.updateZoomPosition(img, e);
          }
        });

        // Add mousemove handler for panning zoomed image
        content.addEventListener('mousemove', (e) => {
          if (!this.isZoomed) return;

          const img = this.querySelector('.product-media-modal__media-item.active img.zoomed');
          if (!img) return;

          this.updateZoomPosition(img, e);
        });

        // Reset zoom when mouse leaves the content area
        content.addEventListener('mouseleave', () => {
          if (this.isZoomed) {
            const img = this.querySelector('.product-media-modal__media-item.active img.zoomed');
            if (img) {
              // Keep zoomed but center the image
              const rect = img.getBoundingClientRect();
              const centerX = rect.width / 2;
              const centerY = rect.height / 2;
              this.applyZoomTransform(img, centerX, centerY, rect);
            }
          }
        });
      }

      updateZoomPosition(img, e) {
        const rect = img.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        this.applyZoomTransform(img, x, y, rect);
      }

      applyZoomTransform(img, x, y, rect) {
        // Calculate percentage position within image
        const xPercent = (x / rect.width) * 100;
        const yPercent = (y / rect.height) * 100;

        // Apply transform with transform-origin at cursor position
        img.style.transformOrigin = `${xPercent}% ${yPercent}%`;
        img.style.transform = `scale(${this.zoomScale})`;
      }
    }
  );
}
